import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MapIcon from "@mui/icons-material/Map";
import FlagIcon from "@mui/icons-material/Flag";
import BackupIcon from "@mui/icons-material/Backup";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { API_URL } from "../config";

const hubs = [
    {
        to: "/tasks",
        title: "Tasks",
        blurb: "Kanban board, priorities, and task calendar.",
        Icon: AssignmentIcon,
    },
    {
        to: "/calendar",
        title: "Calendar",
        blurb: "Meetings, follow-ups, and task timelines in one view.",
        Icon: CalendarMonthIcon,
    },
    {
        to: "/customers",
        title: "Customers",
        blurb: "Accounts, contacts, notes, and linked work.",
        Icon: PeopleIcon,
    },
    {
        to: "/finances",
        title: "Finances",
        blurb: "Monthly income, expenses, and net cash flow.",
        Icon: AccountBalanceIcon,
    },
    {
        to: "/systemsmap",
        title: "Systems map",
        blurb: "Visual map of how systems connect.",
        Icon: MapIcon,
    },
    {
        to: "/roadmap",
        title: "Roadmap",
        blurb: "Planning and future direction.",
        Icon: FlagIcon,
    },
];

function formatBytes(n) {
    const size = Number(n) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const Management = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [backups, setBackups] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const loadBackups = useCallback(async () => {
        if (!isAdmin) return;
        const { data } = await axios.get(`${API_URL}/system/backups`, { withCredentials: true });
        setBackups(data);
    }, [isAdmin]);

    useEffect(() => {
        loadBackups().catch(() => {
            /* non-fatal */
        });
    }, [loadBackups]);

    async function runBackup() {
        setBusy(true);
        setMessage(null);
        try {
            await axios.post(`${API_URL}/system/backups`, { reason: "manual" }, { withCredentials: true });
            setMessage({ severity: "success", text: "Database backup created." });
            await loadBackups();
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Backup failed.",
            });
        } finally {
            setBusy(false);
        }
    }

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: "text.primary" }}>
                Operations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Jump to the main areas of your business workspace.
            </Typography>
            <Grid container spacing={2}>
                {hubs.map(({ to, title, blurb, Icon }) => (
                    <Grid item xs={12} sm={6} md={4} key={to}>
                        <Card sx={{ backgroundColor: "background.paper", height: "100%" }}>
                            <CardActionArea component={RouterLink} to={to} sx={{ height: "100%", alignItems: "stretch" }}>
                                <CardContent>
                                    <Icon sx={{ fontSize: 36, color: "primary.main", mb: 1 }} />
                                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                                        {title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {blurb}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {isAdmin ? (
                <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <BackupIcon color="primary" />
                                <Typography variant="h6">Database backups</Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                Automatic snapshots every {backups?.intervalHours ?? 6} hours (kept:{" "}
                                {backups?.keep ?? 28}). Stored next to the live SQLite file
                                {backups?.dir ? ` (${backups.dir})` : ""}. Customer row edits also keep a revision
                                history on each account.
                            </Typography>
                            {message ? (
                                <Alert severity={message.severity} onClose={() => setMessage(null)}>
                                    {message.text}
                                </Alert>
                            ) : null}
                            <Button
                                variant="contained"
                                startIcon={<BackupIcon />}
                                onClick={runBackup}
                                disabled={busy}
                                sx={{ alignSelf: "flex-start" }}
                            >
                                Backup now
                            </Button>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>File</TableCell>
                                        <TableCell>Size</TableCell>
                                        <TableCell>Modified</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(backups?.items || []).length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3}>
                                                <Typography color="text.secondary">No backups yet.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        (backups.items || []).slice(0, 12).map((row) => (
                                            <TableRow key={row.filename}>
                                                <TableCell>
                                                    <Typography variant="body2" fontFamily="monospace">
                                                        {row.filename}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{formatBytes(row.size)}</TableCell>
                                                <TableCell>
                                                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}
        </Box>
    );
};

export default Management;
